import { useEffect, useRef, useCallback } from 'react';
import { getActiveSystem } from '../config/system';
import { getStackKey } from '../config/stack';
import { yieldScroll } from '../components/ScrollOnNavigate';

// Module-level cache, not affected by React strict mode lifecycle
const cache = {};

// Namespace the cache by the active system so a list's saved page/filters/scroll
// never leak across systems (a dnd-3.5 page-3 state must not restore onto pf-1e).
//
// …and by the active STACK, for exactly the same reason one level down (05 §4,
// 09 Phase B slice B2). A homebrew layer may suppress an entry, rename one, or
// add its own, so "page 3 of Spells filtered to Evocation" is a different page
// under a different stack — restoring the saved page and scroll offset across
// the two lands on the wrong rows. `getStackKey()` is `''` whenever no stack is
// active, which is every session until the Phase D chooser exists, so the key
// is byte-identical to the one this hook has always built.
const keyFor = (routeKey) => `${getActiveSystem()}${getStackKey()}::${routeKey}`;

export function useListNav(routeKey) {
  const key = keyFor(routeKey);
  const data = useRef(cache[key] || null);
  const restored = data.current || {};
  const scrollTarget = useRef(restored.scrollY ?? null);

  // Clear cache after mount
  useEffect(() => {
    delete cache[key];
  }, [key]);

  const restoreScroll = useCallback(() => {
    if (scrollTarget.current != null) {
      const y = scrollTarget.current;
      scrollTarget.current = null;
      yieldScroll();
      requestAnimationFrame(() => window.scrollTo(0, y));
    }
  }, []);

  const saveState = useCallback((filters) => {
    cache[keyFor(routeKey)] = {
      scrollY: window.scrollY,
      ...filters
    };
  }, [routeKey]);

  return { saveState, restoreScroll, restored };
}
